import { useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StarDisplay } from '@/components/common/StarRating';
import ProfilePicture from '@/components/ui/profile-picture';
import ProfileLink from '@/components/Post/ProfileLink';
import { getDisplayName, getRelativeTime } from '@/lib/utils';
import { IProjectMake, RatingData } from './projectMake.d';
import { weightedRating } from './makeRating';

const RATING_LABELS: Record<keyof RatingData, string> = {
  cutExpectation: 'Cut Expectation',
  cutQuality: 'Cut Quality',
  correctFileInfo: 'File Information',
  coolFactor: 'Cool Factor',
};

const PANEL_CLASS = 'rounded-md border border-border bg-card p-4';

type Attachment = IProjectMake['attachments'][number];

function MakeHero({
  image,
  index,
  total,
}: {
  image?: Attachment;
  index: number;
  total: number;
}) {
  return (
    <div className="relative aspect-square rounded-md border border-border bg-card flex items-center justify-center overflow-hidden">
      {image ? (
        <img
          src={image.url}
          alt={image.filename}
          className="w-full h-full object-contain"
        />
      ) : (
        <Camera className="w-12 h-12 text-muted-foreground" />
      )}
      {total > 1 && (
        <span className="absolute bottom-2 right-2 rounded-pill bg-foreground/75 px-2 py-0.5 text-xs font-medium text-background">
          {index + 1} / {total}
        </span>
      )}
    </div>
  );
}

function MakeThumbnails({
  images,
  activeImage,
  onSelect,
}: {
  images: Attachment[];
  activeImage: number;
  onSelect: (index: number) => void;
}) {
  return (
    <div className="flex gap-2 flex-wrap">
      {images.map((attachment, index) => (
        <button
          key={attachment._id}
          type="button"
          onClick={() => onSelect(index)}
          aria-label={`View image ${index + 1}`}
          aria-current={index === activeImage}
          className={`w-14 h-14 rounded-md overflow-hidden border bg-card transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            index === activeImage ? 'border-primary' : 'border-border'
          }`}
        >
          <img
            src={attachment.url}
            alt={attachment.filename}
            className="w-full h-full object-cover"
          />
        </button>
      ))}
    </div>
  );
}

/** Makes outlive their uploader, so a missing account needs a stand-in label. */
function makerIdentity(user?: IProjectMake['user']) {
  const username = user?.username;
  return {
    username,
    label: username ? `@${username}` : 'Unknown maker',
    nameClass: username ? '' : 'text-muted-foreground italic',
  };
}

function MakeAuthor({ make }: { make: IProjectMake }) {
  const { username, label, nameClass } = makerIdentity(make.user);

  return (
    // Only a real account is linkable — a deleted uploader has no profile.
    <ProfileLink username={username} className="flex items-center gap-3">
      <ProfilePicture
        src={make.user?.profilePicture}
        alt={getDisplayName(make.user)}
        className="w-9 h-9 shrink-0"
        username={username}
      />
      <span className="flex flex-col min-w-0">
        <span className={`font-medium truncate ${nameClass}`}>{label}</span>
        <span className="text-sm text-muted-foreground">
          {getRelativeTime(make.createdAt)}
        </span>
      </span>
    </ProfileLink>
  );
}

function MakeRatingPanel({
  total,
  ratings,
}: {
  total: number;
  ratings: RatingData;
}) {
  return (
    <div className={`${PANEL_CLASS} space-y-3`}>
      <div className="flex items-center gap-3 flex-wrap">
        <StarDisplay rating={total} size="sm" />
        <span className="text-lg font-semibold">{total.toFixed(1)}</span>
        <span className="text-sm text-muted-foreground">Total rating</span>
      </div>

      <dl className="space-y-1.5 text-sm border-t border-border pt-3">
        {Object.entries(RATING_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center justify-between gap-4">
            <dt className="text-muted-foreground">{label}</dt>
            <dd>
              <StarDisplay
                rating={ratings[key as keyof RatingData]}
                size="sm"
              />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function MakeSpecs({ printedOn }: { printedOn: IProjectMake['printedOn'] }) {
  const rows = [
    ['Machine', printedOn.cncBrand.name],
    ['Model', printedOn.cncModel.name],
    ['Size', printedOn.name],
  ];

  return (
    <dl className={`${PANEL_CLASS} text-sm space-y-1.5`}>
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-4">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="text-right">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function MakeGallery({ images }: { images: Attachment[] }) {
  const [activeImage, setActiveImage] = useState(0);

  return (
    <div className="space-y-3">
      <MakeHero
        image={images[activeImage]}
        index={activeImage}
        total={images.length}
      />
      {/* Strip only earns its space with more than one image */}
      {images.length > 1 && (
        <MakeThumbnails
          images={images}
          activeImage={activeImage}
          onSelect={setActiveImage}
        />
      )}
    </div>
  );
}

function MakeCaption({ caption }: { caption?: string }) {
  if (!caption) return null;

  return (
    <p className={`${PANEL_CLASS} text-base leading-relaxed`}>{caption}</p>
  );
}

function MakeRating({ make }: { make: IProjectMake }) {
  const total = weightedRating(make.ratings);
  if (!total || !make.ratings) return null;

  return <MakeRatingPanel total={total} ratings={make.ratings} />;
}

function MakeDeleteAction({ onDelete }: { onDelete?: () => void }) {
  if (!onDelete) return null;

  return (
    <div className="flex justify-end">
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:bg-destructive/10"
        onClick={onDelete}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Delete make
      </Button>
    </div>
  );
}

interface MakeDetailProps {
  make: IProjectMake;
  onDelete?: () => void;
}

/** Everything about a single make. Landscape: photos left, details right. */
export default function MakeDetail({ make, onDelete }: MakeDetailProps) {
  return (
    <div className="grid gap-6 md:grid-cols-2 md:items-start">
      <MakeGallery images={make.attachments ?? []} />

      <div className="space-y-4">
        <MakeAuthor make={make} />
        {/* What they wrote is the point of the make — the machine is a footnote. */}
        <MakeCaption caption={make.caption} />
        <MakeRating make={make} />
        <MakeSpecs printedOn={make.printedOn} />
        <MakeDeleteAction onDelete={onDelete} />
      </div>
    </div>
  );
}
